import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { 
  FileText, Plus, Search, Upload, AlertTriangle, TrendingUp, DollarSign, Clock,
  CheckCircle, XCircle, AlertCircle, Calendar, Building2, Target, BarChart3,
  Loader2, Eye, Trash2, RefreshCw, ThumbsUp, ThumbsDown, HelpCircle, Brain,
  FileSearch, Scale, Shield
} from 'lucide-react';
import api from '@/lib/api';
import ModuleInsightsPanel from '@/components/common/ModuleInsightsPanel';
import AIAnalystPanel from '@/components/ai/AIAnalystPanel';
import { useProject } from '@/contexts/ProjectContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import TenderCashflowModeler from '@/components/tenders/TenderCashflowModeler';
import TenderPredictions from '@/components/tenders/TenderPredictions';
import TenderScenarioAnalysis from '@/components/tenders/TenderScenarioAnalysis';

const statusColors = {
  DRAFT: 'bg-slate-500/20 text-slate-400',
  PROCESSING: 'bg-blue-500/20 text-blue-400',
  ANALYZED: 'bg-emerald-500/20 text-emerald-400',
  BID_DECISION_PENDING: 'bg-amber-500/20 text-amber-400',
  BID: 'bg-green-500/20 text-green-400',
  NO_BID: 'bg-red-500/20 text-red-400',
  SUBMITTED: 'bg-purple-500/20 text-purple-400',
  WON: 'bg-emerald-500/20 text-emerald-400',
  LOST: 'bg-slate-500/20 text-slate-400',
  CANCELLED: 'bg-gray-500/20 text-gray-400'
};

const recommendationColors = {
  STRONG_BID: 'bg-emerald-500/20 text-emerald-400',
  BID_WITH_CAUTION: 'bg-amber-500/20 text-amber-400',
  CONDITIONAL_BID: 'bg-orange-500/20 text-orange-400',
  NO_BID: 'bg-red-500/20 text-red-400'
};

const recommendationIcons = {
  STRONG_BID: ThumbsUp,
  BID_WITH_CAUTION: AlertCircle,
  CONDITIONAL_BID: HelpCircle,
  NO_BID: ThumbsDown
};

const formatCurrency = (value, currency = 'USD') => {
  if (!value && value !== 0) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    maximumFractionDigits: 0
  }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getDaysRemaining = (deadline) => {
  if (!deadline) return null;
  const days = Math.ceil((new Date(deadline) - new Date()) / (1000 * 60 * 60 * 24));
  return days;
};

export default function TenderAnalytics() {
  const navigate = useNavigate();
  const { projectId } = useProject();
  // Tenders is tenant-scoped, but we anchor the AI Analyst to whichever
  // project the user currently has selected. Fall back to localStorage when
  // ProjectContext hasn't hydrated yet (e.g. direct navigation to /tenders).
  const analystProjectId = projectId || (typeof window !== 'undefined' && localStorage.getItem('ciq_selected_project')) || null;
  
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showNewTenderDialog, setShowNewTenderDialog] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  
  const [newTender, setNewTender] = useState({
    tender_name: '',
    client_name: '',
    tender_reference: '',
    project_type: 'CONSTRUCTION',
    procurement_method: 'OPEN',
    contract_type: 'LUMP_SUM',
    country_jurisdiction: '',
    submission_deadline: '',
    estimated_value: '',
    currency: 'USD',
    description: ''
  });

  useEffect(() => {
    fetchTenders();
    fetchDashboard();
  }, [projectId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTenders = async () => {
    try {
      setLoading(true);
      const params = projectId ? { params: { project_id: projectId } } : {};
      const response = await api.get('/tenders/', params);
      setTenders(response.data || []);
    } catch (error) {
      console.error('Error fetching tenders:', error);
      toast.error('Failed to load tenders');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/tenders/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    }
  };

  const handleCreateTender = async () => {
    try {
      const tenderData = {
        ...newTender,
        estimated_value: parseFloat(newTender.estimated_value) || null,
        submission_deadline: newTender.submission_deadline ? new Date(newTender.submission_deadline).toISOString() : null
      };
      
      await api.post('/tenders/', tenderData);
      toast.success('Tender created successfully');
      setShowNewTenderDialog(false);
      setNewTender({
        tender_name: '',
        client_name: '',
        tender_reference: '',
        project_type: 'CONSTRUCTION',
        procurement_method: 'OPEN',
        contract_type: 'LUMP_SUM',
        country_jurisdiction: '',
        submission_deadline: '',
        estimated_value: '',
        currency: 'USD',
        description: ''
      });
      fetchTenders();
      fetchDashboard();
    } catch (error) {
      console.error('Error creating tender:', error);
      toast.error('Failed to create tender');
    }
  };

  const handleDeleteTender = async (tenderId, e) => {
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this tender?')) return;
    
    try {
      await api.delete(`/tenders/${tenderId}`);
      toast.success('Tender deleted');
      fetchTenders();
      fetchDashboard();
    } catch (error) {
      console.error('Error deleting tender:', error);
      toast.error('Failed to delete tender');
    }
  };

  const filteredTenders = tenders.filter(tender => {
    const matchesSearch = 
      tender.tender_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tender.client_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      tender.tender_reference?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || tender.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="tender-analytics-module">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Tender Analytics</h1>
            <p className="text-sm text-slate-400 mt-1">AI-powered tender risk intelligence and bid/no-bid decision support</p>
          </div>
          <Button 
            onClick={() => setShowNewTenderDialog(true)}
            className="bg-gradient-to-r from-[#1E4E8C] to-[#6B5CE7] hover:opacity-90"
            data-testid="new-tender-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Tender
          </Button>
        </div>

        {/* AI Analyst Panel */}
        {analystProjectId && (
          <AIAnalystPanel projectId={analystProjectId} moduleKey="TENDERS" defaultOpen={false} compact={true} />
        )}

        {/* Dashboard KPIs */}
        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card className="bg-[#1F2937] border-[#3A3F4A]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-[#1E4E8C]/20">
                      <FileSearch className="w-5 h-5 text-[#1E4E8C]" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Active Tenders</p>
                      <p className="text-2xl font-bold text-white">{dashboard.total_tenders || 0}</p>
                      <p className="text-xs text-slate-400">{dashboard.by_status?.ANALYZED || 0} analyzed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card className="bg-[#1F2937] border-[#3A3F4A]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-emerald-500/20">
                      <DollarSign className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Pipeline Value</p>
                      <p className="text-2xl font-bold text-white">{formatCurrency(dashboard.total_pipeline_value)}</p>
                      <p className="text-xs text-slate-400">total opportunity</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <Card className="bg-[#1F2937] border-[#3A3F4A]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-green-500/20">
                      <ThumbsUp className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Recommended Bids</p>
                      <p className="text-2xl font-bold text-white">
                        {(dashboard.by_recommendation?.STRONG_BID || 0) + (dashboard.by_recommendation?.BID_WITH_CAUTION || 0)}
                      </p>
                      <p className="text-xs text-slate-400">{dashboard.by_recommendation?.STRONG_BID || 0} strong bids</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
              <Card className="bg-[#1F2937] border-[#3A3F4A]">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-lg bg-amber-500/20">
                      <Clock className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Upcoming Deadlines</p>
                      <p className="text-2xl font-bold text-white">{dashboard.upcoming_deadlines?.length || 0}</p>
                      <p className="text-xs text-slate-400">within 30 days</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        )}

        {/* Tabs: Pipeline / Cashflow / Predictions / Scenarios */}
        <Tabs defaultValue="pipeline" className="space-y-4">
          <TabsList className="bg-[#1F2937] border border-[#3A3F4A]">
            <TabsTrigger value="pipeline" data-testid="tab-pipeline">Pipeline</TabsTrigger>
            <TabsTrigger value="cashflow" data-testid="tab-cashflow">Cashflow Modeler</TabsTrigger>
            <TabsTrigger value="predictions" data-testid="tab-predictions">Predictions &amp; Forecast</TabsTrigger>
            <TabsTrigger value="scenarios" data-testid="tab-scenarios">Scenario Analysis</TabsTrigger>
          </TabsList>

          <TabsContent value="pipeline" className="space-y-6">
        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              data-testid="tender-search"
              placeholder="Search tenders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#1F2937] border-[#3A3F4A] text-white"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 bg-[#1F2937] border-[#3A3F4A] text-white" data-testid="status-filter">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#1F2937] border-[#3A3F4A]">
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="DRAFT">Draft</SelectItem>
              <SelectItem value="PROCESSING">Processing</SelectItem>
              <SelectItem value="ANALYZED">Analyzed</SelectItem>
              <SelectItem value="BID_DECISION_PENDING">Decision Pending</SelectItem>
              <SelectItem value="BID">Bid</SelectItem>
              <SelectItem value="NO_BID">No Bid</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tenders Table */}
        <Card className="bg-[#1F2937] border-[#3A3F4A]">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-[#1E4E8C] animate-spin" />
              </div>
            ) : filteredTenders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <FileSearch className="w-12 h-12 text-slate-600 mb-4" />
                <p className="text-slate-400 mb-2">No tenders found</p>
                <p className="text-xs text-slate-500 mb-4">Create your first tender to get started with AI analysis</p>
                <Button 
                  onClick={() => setShowNewTenderDialog(true)}
                  variant="outline" 
                  className="border-[#3A3F4A] text-slate-300"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Tender
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#3A3F4A]">
                      <th className="text-left text-xs text-slate-500 font-medium px-4 py-3 uppercase tracking-wider">Tender</th>
                      <th className="text-left text-xs text-slate-500 font-medium px-4 py-3 uppercase tracking-wider">Client</th>
                      <th className="text-left text-xs text-slate-500 font-medium px-4 py-3 uppercase tracking-wider">Value</th>
                      <th className="text-left text-xs text-slate-500 font-medium px-4 py-3 uppercase tracking-wider">Deadline</th>
                      <th className="text-left text-xs text-slate-500 font-medium px-4 py-3 uppercase tracking-wider">Status</th>
                      <th className="text-left text-xs text-slate-500 font-medium px-4 py-3 uppercase tracking-wider">AI Recommendation</th>
                      <th className="text-right text-xs text-slate-500 font-medium px-4 py-3 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTenders.map((tender, index) => {
                      const daysRemaining = getDaysRemaining(tender.submission_deadline);
                      const RecommendationIcon = tender.bid_decision?.recommendation ? 
                        recommendationIcons[tender.bid_decision.recommendation] : HelpCircle;
                      
                      return (
                        <motion.tr 
                          key={tender.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.03 }}
                          className="border-b border-[#3A3F4A] hover:bg-white/[0.02] cursor-pointer"
                          onClick={() => navigate(`/tenders/${tender.id}`)}
                          data-testid={`tender-row-${tender.id}`}
                        >
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-sm text-white font-medium">{tender.tender_name}</p>
                              <p className="text-xs text-slate-500">{tender.tender_reference || tender.project_type}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-slate-300">{tender.client_name}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-white">
                              {formatCurrency(tender.estimated_value?.amount, tender.estimated_value?.currency)}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-sm text-white">{formatDate(tender.submission_deadline)}</p>
                              {daysRemaining !== null && (
                                <p className={`text-xs ${daysRemaining <= 7 ? 'text-red-400' : daysRemaining <= 14 ? 'text-amber-400' : 'text-slate-500'}`}>
                                  {daysRemaining <= 0 ? 'Expired' : `${daysRemaining} days left`}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={statusColors[tender.status] || 'bg-slate-500/20 text-slate-400'}>
                              {tender.status === 'PROCESSING' && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                              {tender.status?.replace(/_/g, ' ')}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            {tender.bid_decision?.recommendation ? (
                              <div className="flex items-center gap-2">
                                <Badge className={recommendationColors[tender.bid_decision.recommendation] || 'bg-slate-500/20 text-slate-400'}>
                                  <RecommendationIcon className="w-3 h-3 mr-1" />
                                  {tender.bid_decision.recommendation.replace(/_/g, ' ')}
                                </Badge>
                                <span className="text-xs text-slate-500">
                                  Score: {tender.bid_decision.overall_score?.toFixed(0)}
                                </span>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-500">Pending analysis</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 text-slate-400 hover:text-white"
                                onClick={() => navigate(`/tenders/${tender.id}`)}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 text-slate-400 hover:text-red-400"
                                onClick={(e) => handleDeleteTender(tender.id, e)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
          </TabsContent>

          {/* Cashflow Modeler */}
          <TabsContent value="cashflow">
            <TenderCashflowModeler tenders={tenders} />
          </TabsContent>

          {/* Predictions & Forecast */}
          <TabsContent value="predictions">
            <TenderPredictions />
          </TabsContent>

          {/* Scenario Analysis */}
          <TabsContent value="scenarios">
            <TenderScenarioAnalysis tenders={tenders} />
          </TabsContent>
        </Tabs>

        {/* New Tender Dialog */}
        <Dialog open={showNewTenderDialog} onOpenChange={setShowNewTenderDialog}>
          <DialogContent className="bg-[#1F2937] border-[#3A3F4A] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Create New Tender</DialogTitle>
              <DialogDescription className="text-slate-400">
                Enter tender details. Upload documents after creation to start AI analysis.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label className="text-slate-300">Tender Name *</Label>
                  <Input
                    data-testid="tender-name-input"
                    placeholder="e.g., Metro Line Extension Phase 2"
                    value={newTender.tender_name}
                    onChange={(e) => setNewTender({ ...newTender, tender_name: e.target.value })}
                    className="bg-[#0A0E1A] border-[#3A3F4A] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Client Name *</Label>
                  <Input
                    data-testid="client-name-input"
                    placeholder="e.g., Ministry of Transport"
                    value={newTender.client_name}
                    onChange={(e) => setNewTender({ ...newTender, client_name: e.target.value })}
                    className="bg-[#0A0E1A] border-[#3A3F4A] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Tender Reference</Label>
                  <Input
                    placeholder="e.g., RFP-2026-001"
                    value={newTender.tender_reference}
                    onChange={(e) => setNewTender({ ...newTender, tender_reference: e.target.value })}
                    className="bg-[#0A0E1A] border-[#3A3F4A] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Project Type</Label>
                  <Select 
                    value={newTender.project_type} 
                    onValueChange={(v) => setNewTender({ ...newTender, project_type: v })}
                  >
                    <SelectTrigger className="bg-[#0A0E1A] border-[#3A3F4A] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1F2937] border-[#3A3F4A]">
                      <SelectItem value="CONSTRUCTION">Construction</SelectItem>
                      <SelectItem value="EPC">EPC</SelectItem>
                      <SelectItem value="IT">IT</SelectItem>
                      <SelectItem value="SERVICES">Services</SelectItem>
                      <SelectItem value="SUPPLY">Supply</SelectItem>
                      <SelectItem value="MIXED">Mixed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Contract Type</Label>
                  <Select 
                    value={newTender.contract_type} 
                    onValueChange={(v) => setNewTender({ ...newTender, contract_type: v })}
                  >
                    <SelectTrigger className="bg-[#0A0E1A] border-[#3A3F4A] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1F2937] border-[#3A3F4A]">
                      <SelectItem value="LUMP_SUM">Lump Sum</SelectItem>
                      <SelectItem value="UNIT_RATE">Unit Rate</SelectItem>
                      <SelectItem value="COST_PLUS">Cost Plus</SelectItem>
                      <SelectItem value="EPC">EPC</SelectItem>
                      <SelectItem value="DESIGN_BUILD">Design Build</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Estimated Value</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 50000000"
                    value={newTender.estimated_value}
                    onChange={(e) => setNewTender({ ...newTender, estimated_value: e.target.value })}
                    className="bg-[#0A0E1A] border-[#3A3F4A] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Currency</Label>
                  <Select 
                    value={newTender.currency} 
                    onValueChange={(v) => setNewTender({ ...newTender, currency: v })}
                  >
                    <SelectTrigger className="bg-[#0A0E1A] border-[#3A3F4A] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1F2937] border-[#3A3F4A]">
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                      <SelectItem value="AED">AED</SelectItem>
                      <SelectItem value="SAR">SAR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Submission Deadline</Label>
                  <Input
                    type="datetime-local"
                    value={newTender.submission_deadline}
                    onChange={(e) => setNewTender({ ...newTender, submission_deadline: e.target.value })}
                    className="bg-[#0A0E1A] border-[#3A3F4A] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Country / Jurisdiction</Label>
                  <Input
                    placeholder="e.g., United Arab Emirates"
                    value={newTender.country_jurisdiction}
                    onChange={(e) => setNewTender({ ...newTender, country_jurisdiction: e.target.value })}
                    className="bg-[#0A0E1A] border-[#3A3F4A] text-white"
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label className="text-slate-300">Description</Label>
                  <Textarea
                    placeholder="Brief description of the tender..."
                    value={newTender.description}
                    onChange={(e) => setNewTender({ ...newTender, description: e.target.value })}
                    className="bg-[#0A0E1A] border-[#3A3F4A] text-white"
                    rows={3}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button 
                variant="ghost" 
                onClick={() => setShowNewTenderDialog(false)}
                className="text-slate-400"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleCreateTender}
                className="bg-gradient-to-r from-[#1E4E8C] to-[#6B5CE7]"
                data-testid="create-tender-btn"
                disabled={!newTender.tender_name || !newTender.client_name}
              >
                Create Tender
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ModuleInsightsPanel projectId={analystProjectId || 'proj_001'} moduleKey="tender" color="#A78BFA" deepLinkBase="/tenders" />
      </div>
    </DashboardLayout>
  );
}
